<template>
  <div class="row submissions">
    <div class="small-12">
      <div class="submissions__topbar">
        <Sticky
          align="right"
          :stickyTop="40"
          specifyWidth="auto"
        >
          <template
            v-for="(item, index) in engineerButtons"
          >
            <HKLButton
              :key="index"
              :color="item.color"
              :disable="item.disable"
              @click="item.func"
            >
              {{ item.title }}
            </HKLButton>
          </template>
        </Sticky>
      </div>
    </div>
    <div class="row small-12 submissions__container">
      <transition name="fade">
        <div
          v-show="isCollapse"
          class="left-content"
        >
          <TenantProjectDocuments
            :projectDocsSrcId="projectDocsSrcId"
            :enableEdit="canEditDocument"
            :enableUpload="canUploadDocs"
          />
        </div>
      </transition>
      <div
        class="sticky-box"
        :class="{ 'collapse': !isCollapse }"
      >
        <Sticky
          align="none"
          specifyWidth="auto"
          :stickyTop="40"
        >
          <i
            class="sticky-angle fa"
            :class="[isCollapse ? 'fa-angle-left' : 'fa-angle-right']"
            @click="triggerCollapse"
          />
        </Sticky>
      </div>
      <div class="right-content">
        <div class="submission-comments-container">
          <SubmissionTabsContainer
            v-if="isTenant"
            :tabNames="tabNames"
          >
            <CommentsView
              v-if="tenantVisibleSubmissions.length"
              slot="tab-0"
            />
            <Guideline
              :slot="!tenantVisibleSubmissions.length ? 'tab-0' : 'tab-1'"
            />
          </SubmissionTabsContainer>
          <SubmissionTabsContainer
            v-else
            :tabNames="tabNames"
          >
            <Comments
              ref="comments"
              slot="tab-0"
              :canCommentDesign="canCommentDesign && canEdit"
              :canCommentTech="canCommentTech && canEdit"
              :isTenant="isTenant"
            />
            <Guideline
              slot="tab-1"
              :enableEdit="canEditGuideline"
            />
          </SubmissionTabsContainer>
        </div>
      </div>
      <CpCommentSubmitModal
        ref="cpCommentSubmitModal"
        :confirmCallback="confirmSubmitCPComment"
      />
      <PreviewSubmissionModal ref="previewSubmissionModal" />
      <ReassignSubmissionModal
        ref="reassignSubmissionModal"
        :confirmCallback="confirmReassign"
      />
      <TenantNextReviewerModal
        ref="nextReviewerModal"
        :confirmCallback="passToNextViewer"
      />
      <CancelBatchModal ref="cancelBatchModalRef" />
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { reactive, toRefs, computed, unref } from 'vue-demi'
import { mapGetters, mapActions } from 'vuex'
import Sticky from '@/controls/Sticky'
import HKLButton from '@/controls/Button/HKLButton'
import TenantProjectDocuments from '@/modules/tenantProjects/documents/TenantProjectDocuments'
import Comments from '@/modules/tenantProjects/submissions/Comments'
import CommentsView from '@/modules/tenantProjects/CommentsView'
import Guideline from '@/modules/tenantProjects/Guideline'
import CpCommentSubmitModal from '@/modules/tenantProjects/submissions/CpCommentSubmitModal'
import PreviewSubmissionModal from '@/modules/tenantProjects/submissions/PreviewSubmissionModal'
import TenantNextReviewerModal from '@/modules/tenantProjects/submissions/TenantNextReviewerModal'
import ReassignSubmissionModal from '@/modules/tenantProjects/submissions/ReassignSubmissionModal'
import SubmissionTabsContainer from '@/modules/tenantProjects/submissions/SubmissionTabsContainer'
import EventBus from '@/utils/eventBus'
import { sendEmail } from '@/api/postman/request'
import { updateTenantSubmissionByPath } from '@/api/tenantProjects/request'
import { CommentTypes, CommentObject, CommentActionTypes } from '@/modules/tenantProjects/submissions/consts'
import { utils as formUtils } from '@/plugin/InputChange'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { EmailTypes } from '@/utils/consts'
import { tenantProjectStatus, DesignBatchStatus, tenantProjectTypes } from '@/utils/locale'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useStoreGetters } from '@/store'
import CancelBatchModal from './CancelBatchModal'
import { filterExternalUserVisibleSubmissions } from '@/modules/tenantProjects/utils'

export default {
  components: {
    HKLButton,
    CancelBatchModal,
    TenantProjectDocuments,
    SubmissionTabsContainer,
    Comments,
    CommentsView,
    Guideline,
    PreviewSubmissionModal,
    CpCommentSubmitModal,
    ReassignSubmissionModal,
    TenantNextReviewerModal,
    Sticky,
  },
  setup () {
    const { currentTenantProjectDocs, activeSubmissionIndex } = useStoreGetters('tenantProject', ['currentTenantProjectDocs', 'activeSubmissionIndex'])
    const cancelBatchModals = useRefModal()
    const setupData = reactive({
      activeSubmissionIndex,
      isCurBatchDocsExcluded: computed(() => {
        return unref(currentTenantProjectDocs)
          .filter(item => parseInt(item.batchNum, 10) === unref(activeSubmissionIndex))
          .every(item => item.metaData.isNa)
      }),
      cancelBatchModalRef: cancelBatchModals.modal,
      openCancelBatchModal: () => {
        cancelBatchModals.open()
      },
      isCollapse: true,
      isDisableToSubmitCPComment: false,
    })
    return {
      ...toRefs(setupData),
    }
  },
  computed: {
    ...mapGetters('tenantProject', ['currentTenantProject', 'currentTenantSubmission', 'activeSubmission', 'canTenantUpdate', 'hasPendingSubmission', 'currentDesignReviewer', 'currentTechReviewer', 'currentTenantProjectDocs']),
    ...mapGetters('user', ['isTenant', 'currentUser', 'isHKlandUser']),
    projectId () {
      return this.$router.currentRoute.params.id
    },
    designComment () {
      return JSON.parse(JSON.stringify(lodash.get(this.currentTenantSubmission, 'designComment') || {}))
    },
    techComment () {
      return (this.currentTenantSubmission && this.currentTenantSubmission.techComment) || {}
    },
    canCommentDesign () {
      return aclHelper.getAccess(tmsPermsTypes.project.commentDesign)
    },
    canCommentTech () {
      return aclHelper.getAccess(tmsPermsTypes.project.commentTech)
    },
    canCreateUpdate () {
      return aclHelper.getAccess(tmsPermsTypes.project.createUpdate)
    },
    canPreview () {
      return aclHelper.getAccess(tmsPermsTypes.project.preview)
    },
    canSend () {
      return aclHelper.getAccess(tmsPermsTypes.project.send)
    },
    isAllowablePM () {
      return this.isDesignDisapprove && this.canSend
    },
    isTechCommentStarted () {
      return lodash.get(this.activeSubmission, 'techManagedBy')
    },
    projectDocsSrcId () {
      return lodash.get(this.currentTenantProject, 'projectDocsSrcId') || ''
    },
    submissions () {
      return lodash.get(this.currentTenantProject, 'submissions') || []
    },
    tenantVisibleSubmissions () {
      return filterExternalUserVisibleSubmissions(this.submissions)
    },
    canEditGuideline () {
      return this.canEdit && this.hasPendingSubmission && this.isTechCommentStarted &&
        this.canCommentTech && ((this.isTechPic && !this.isDesignDisapprove) || this.isAllowablePM) &&
        !lodash.get(this.currentTenantSubmission, 'completedDate')
    },
    isDesignPic () {
      return this.currentDesignReviewer && this.currentDesignReviewer.id === this.currentUser.id
    },
    isTechPic () {
      return this.currentTechReviewer && this.currentTechReviewer.id === this.currentUser.id
    },
    isDesignDisapprove () {
      return lodash.get(this.designComment, 'comment', '') === DesignBatchStatus.DISAPPROVE
    },
    showReassginBtn () {
      return this.currentTenantProject.type === tenantProjectTypes.Fitout && this.hasPendingSubmission && this.canCommentDesign
    },
    engineerButtons () {
      const btnMap = {
        cancelBatchButton: {
          shouldShowBtn: () => (
            this.hasPendingSubmission &&
            this.isCurBatchDocsExcluded &&
            this.canCommentTech
          ),
          btnParams: () => ({
            title: this.$t('tenantProjects.submission.cancelBatch'),
            color: 'darkblue',
            func: this.openCancelBatchModal,
          }),
        },
        saveButton: {
          shouldShowBtn: () => (
            (this.isDesignPic || this.isTechPic ||
            (this.isAllowablePM && this.isTechCommentStarted)) &&
            this.hasPendingSubmission
          ),
          btnParams: () => ({
            title: this.$t('action.save'),
            color: 'darkblue',
            func: this.save,
          }),
        },
        previewButton: {
          shouldShowBtn: () => (
            this.hasPendingSubmission &&
            this.canPreview &&
            this.isTechCommentStarted
          ),
          btnParams: () => ({
            title: this.canSend ? this.$t('action.preview_send') : this.$t('action.preview'),
            color: 'orange',
            func: this.openPreview,
          }),
        },
        submitButton: {
          shouldShowBtn: () => (this.showReassginBtn && this.isDesignPic),
          btnParams: () => ({
            title: this.$t('action.submit'),
            color: 'orange',
            func: this.submitCPComment,
            disable: this.isDisableToSubmitCPComment,
          }),
        },
        commentDesignButtons: {
          shouldShowBtn: () => (this.showReassginBtn),
          btnParams: () => ({
            title: this.$t('action.reAssign'),
            color: 'orange',
            func: this.reassignSubmission,
          }),
        },
        commentTechButtons: {
          shouldShowBtn: () => (
            this.hasPendingSubmission &&
            this.canCommentTech &&
            !this.isDesignDisapprove
          ),
          btnParams: () => ({
            title: this.$t('tenantProjects.submission.nextReviewer'),
            color: 'orange',
            func: this.openSelect,
          }),
        },
      }
      return Object.values(btnMap).reduce((rs, item) => {
        if (item.shouldShowBtn() && this.isHKlandUser) {
          rs.push(item.btnParams())
        }
        return rs
      }, [])
    },
    tabNames () {
      let tabs = [this.$t('tenantProjects.guideline')]
      if (!this.isTenant || this.tenantVisibleSubmissions.length) {
        tabs.unshift(this.$t('tenantProjects.comments'))
      }
      return tabs
    },
    status () {
      return lodash.get(this.currentTenantProject, 'status', '').toLocaleUpperCase()
    },
    canEdit () {
      return this.status !== tenantProjectStatus.CANCELLED && this.status !== tenantProjectStatus.WITHDRAWN
    },
    canEditDocument () {
      if (this.activeSubmission && !this.isTenant) {
        return this.canEdit && (this.currentUser.id === this.activeSubmission.designManagedBy || this.currentUser.id === this.activeSubmission.techManagedBy)
      } else {
        return this.canEdit
      }
    },
    canUploadDocs () {
      if (!this.isTenant) {
        return this.canEdit && (this.canCommentDesign || this.canCommentTech || this.canCreateUpdate || this.canPreview || this.canSend)
      } else {
        return this.canEdit
      }
    },
  },
  watch: {
    currentTenantProjectDocs () {
      let temp = true
      if (this && this.currentTenantProjectDocs && this.currentTenantProjectDocs.length > 0) {
        this.currentTenantProjectDocs.forEach(doc => {
          if (doc.metaData && doc.metaData.submitted) {
            const isDesign = lodash.get(doc, 'metaData.isDesign', false)
            const isTechnical = lodash.get(doc, 'metaData.isTechnical', false)
            const isNa = lodash.get(doc, 'metaData.isNa', false)
            if (!(isDesign || isTechnical || isNa)) {
              temp = false
            }
          }
        })
      }
      this.isDisableToSubmitCPComment = !temp
    },
  },
  methods: {
    ...mapActions('tenantProject', ['updateCurrentTenantSubmission', 'loadTenantProject', 'updateCurrentStateSubmissionByPath', 'logSubmissionAction', 'isUpdateable', 'updateCurSubmissionExpDesign']),
    async save () {
      const isUpdateAble = await this.isUpdateable(this.projectId)
      if (isUpdateAble) {
        await this.updateCurrentTenantSubmission({})
        await this.loadTenantProject(this.projectId)
      }
      formUtils.resetDirty()
      EventBus.$emit('disable-tech-comments-edit')
      return isUpdateAble
    },
    async openPreview () {
      await this.$refs.previewSubmissionModal.openModal()
    },
    async openSelect () {
      await this.updateCurSubmissionExpDesign({needNotificate: false})
      await this.$refs.nextReviewerModal.openModal()
    },
    validate () {
      let validForm = true
      if (this.currentDesignReviewer === this.currentUser.id) {
        validForm = validForm && this.$refs.cpCommentSubmitModal.validate()
      }
      return validForm
    },
    tenantSubmit () {
      EventBus.$emit('tenant_submission_submitted')
    },
    triggerCollapse () {
      this.isCollapse = !this.isCollapse
    },
    async submitCPComment () {
      const request = {
        path: ['designComment', 'description'],
        value: this.designComment.description,
      }
      await this.updateCurrentTenantSubmission({needNotificate: false})
      await updateTenantSubmissionByPath(this.currentTenantSubmission.id, request)
      await this.$refs.cpCommentSubmitModal.openModal()
    },
    async confirmSubmitCPComment (err) {
      if (err || !this.validate()) return
      this.logSubmissionAction({
        actionType: CommentActionTypes.REVIEW,
        commentTypes: [CommentTypes.DESIGN],
        comment: this.designComment.comment,
      })
      this.logSubmissionAction({
        actionType: CommentActionTypes.UPDATE,
        commentTypes: [CommentTypes.DESIGN],
        commentObject: CommentObject.COMMENT,
        comment: this.designComment.comment,
      })
      if (this.currentDesignReviewer && this.currentDesignReviewer.id === this.currentUser.id) {
        this.updateCurrentStateSubmissionByPath({path: ['designComment', 'submittedDate'], newValue: new Date()})
        this.updateCurrentStateSubmissionByPath({path: ['submittedDesignComment'],
          newValue: lodash.cloneDeep(this.designComment)})
      }
      const isDisapprove = this.designComment.comment === DesignBatchStatus.DISAPPROVE
      const isUpdateAble = await this.save()
      if (isDisapprove && isUpdateAble) {
        await sendEmail(EmailTypes.tenantProjects.batchStatusChanged, false)
      }
    },
    async reassignSubmission () {
      await this.$refs.reassignSubmissionModal.openModal()
    },
    async confirmReassign (err, newUserId) {
      if (err) return
      this.logSubmissionAction({
        actionType: CommentActionTypes.UPDATE,
        commentTypes: [CommentTypes.DESIGN],
        commentObject: CommentObject.COMMENT,
        comment: this.designComment.comment,
      })
      this.updateCurrentStateSubmissionByPath({
        path: ['designManagedBy'],
        newValue: newUserId,
      })
      const isUpdateAble = await this.save()
      if (isUpdateAble) {
        await sendEmail(EmailTypes.tenantProjects.reassign, null, false)
      }
    },
    async passToNextViewer (err, newUserId) {
      if (err) return
      this.updateCurrentStateSubmissionByPath({
        path: ['techManagedBy'],
        newValue: newUserId,
      })
      this.logSubmissionAction({
        actionType: CommentActionTypes.REVIEW,
        commentTypes: [CommentTypes.TECH],
      })
      if (this.currentTechReviewer && this.currentTechReviewer.id === this.currentUser.id) {
        this.updateCurrentStateSubmissionByPath({path: ['techComment', 'submittedDate'], newValue: new Date()})
        this.updateCurrentStateSubmissionByPath({path: ['submittedTechComment'],
          newValue: lodash.cloneDeep(this.techComment)})
      }
      const isUpdateAble = await this.save()
      if (isUpdateAble) {
        await sendEmail(EmailTypes.tenantProjects.nextReviewer, null, false)
      }
    },
  },
}
</script>

<style lang="scss">
.submissions {
  display: flex;
  .block {
    height: 48px;
    width: 48px;
    position: absolute;
    background-size: contain !important;
  }
  @keyframes fade-silde {
    0% {
      opacity: 0;
      display: none;
      flex: 0;
    }
    100% {
      opacity: .8;
      flex: 1;
  }
  }
  .fade-enter-active {
    animation: fade-silde .3s;
  }
  .fade-leave-active {
    animation: fade-silde .2s reverse;
  }
  .extendStickyClass {
    & > div {
      left: auto !important;
    }
  }
}
.submission-comments-container {
  flex: 1;
  .tabs-title a {
    padding: 0;
    line-height: 48px;
  }
}
</style>

<style lang="scss" scoped>
.submissions {
  &__container {
    position: relative;
    i.sticky-angle.fa {
      font-size: 40px;
      margin: 130px 0 0;
      cursor: pointer;
      &.fa-angle-right {
        margin-left: -20px;
      }
    }
  }
  .sticky-box {
    width: 15px;
    margin: 0 5px;
    &.collapse {
      width: auto;
      margin: 0;
    }
  }
  .right-content {
    flex: 1;
    display: flex;
  }
  .left-content {
    flex: 1;
    z-index: 1;
  }
}

.submission-tabs-panel {
  background-color: white;
  padding: 24px 10px;
  box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
}
.tabs-title {
  box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
  a {
    margin: 0;
    background-color: white;
    height: 48px;
    min-height: 48px;
    line-height: 48px;
    padding-left: 15px;
    font-weight: bold;
    font-size: 14px;
    padding: 0 10px;
  }
}
</style>
